import { get, range } from 'lodash'
import * as Yup from 'yup'
import i18n from 'i18next'

import * as Nope from 'lib/utils/validation/Nope'
import Vehicle from './Vehicle'
import {
    getVehicleDisplayName,
    getEngineSizes,
    VEHICLE_TYPES,
    getVehicleMakeQuickOptions,
    getAlpahebeticalOptionGroups
} from 'lib/utils/store/vehicle'

import getOptionsMachineVehicleMake from 'graphql/options/getOptionsMachineVehicleMake.gql'
import getOptionsMachineVehicleCommon from 'graphql/options/getOptionsMachineVehicleCommon.gql'
import getOptionsMachineVehicleDriveType from 'graphql/options/getOptionsMachineVehicleDriveType.gql'

const yearOptions = range(new Date().getFullYear() + 1, 1899).map(y => ({ name: y, value: y }))

export default class MachineVehicle extends Vehicle {
    static create (vehicle) {
        return new MachineVehicle(vehicle)
    }

    /**
     * Common data
     */

    getYear () {
        return get(this, 'vehicle.verticalData.year', null)
    }

    getMakeId () {
        return get(this, 'vehicle.verticalData.make.id', null)
    }

    getMakeName () {
        return get(this, 'vehicle.verticalData.make.name', null)
    }

    getModelTypeName () {
        return get(this, 'vehicle.verticalData.modelTypeName')
    }

    getMakeAndModelName () {
        const make = this.getMakeName() || ''
        const model = this.getModelTypeName() || ''
        return `${make} ${model}`.trim()
    }

    getMakeModelAndYear () {
        const year = this.getYear()
        const makeName = this.getMakeName()
        const modelName = this.getModelTypeName()

        return (makeName || modelName || year)
            ? `${makeName || '-'}, ${modelName || '-'}, ${year || '-'}`
            : null
    }

    getEngineModel () {
        return get(this, 'vehicle.verticalData.engineModel', null)
    }

    getColorId () {
        return get(this, 'vehicle.verticalData.color.id', null)
    }

    getColorName (langCode) {
        return get(this, `vehicle.verticalData.color[${langCode}]`, null)
    }

    getColorTypeId () {
        return get(this, 'vehicle.verticalData.colorType.id', null)
    }

    getColorTypeName (langCode) {
        return get(this, `vehicle.verticalData.colorType[${langCode}]`, null)
    }

    getVinNumber () {
        return get(this, 'vehicle.verticalData.vin', null)
    }

    getRearAxelId () {
        return get(this, 'vehicle.verticalData.rearAxelModel.id', null)
    }

    getRearAxel (langCode) {
        return get(this, `vehicle.verticalData.rearAxelModel[${langCode}]`, null)
    }

    getSuspensionTypeId () {
        return get(this, 'vehicle.verticalData.suspensionType.id', null)
    }

    getSuspensionType (langCode) {
        return get(this, `vehicle.verticalData.suspensionType[${langCode}]`, null)
    }

    getBreakTypeId () {
        return get(this, 'vehicle.verticalData.breakType.id', null)
    }

    getBreakType (langCode) {
        return get(this, `vehicle.verticalData.breakType[${langCode}]`, null)
    }

    /**
     * Technical data
     */

    getFuelTypeId () {
        return get(this, 'vehicle.verticalData.fuelType.id', null)
    }

    getFuelType (langCode) {
        return get(this, `vehicle.verticalData.fuelType[${langCode}]`, null)
    }

    getEngineSize () {
        return get(this, 'vehicle.verticalData.engineSize', null)
    }

    getDriveTypeId () {
        return get(this, 'vehicle.verticalData.driveType.id', null)
    }

    getDriveType (langCode) {
        return get(this, `vehicle.verticalData.driveType[${langCode}]`, null)
    }

    getGearTypeId () {
        return get(this, 'vehicle.verticalData.gearType.id', null)
    }

    getGearType (langCode) {
        return get(this, `vehicle.verticalData.gearType[${langCode}]`, null)
    }

    getPower () {
        return get(this, 'vehicle.verticalData.power', null)
    }

    getTorque () {
        return get(this, 'vehicle.verticalData.torque', null)
    }

    getPowerAndTorque () {
        const power = this.getPower()
        const torque = this.getTorque()
        return power || torque ? `${power ? `${power} (kW)` : '- '}, ${torque ? `${torque} (Nm)` : '-'}` : null
    }

    getGrossWeight () {
        return get(this, 'vehicle.verticalData.weight', null)
    }

    getSerialNumber () {
        return get(this, 'vehicle.verticalData.serialNumber', null)
    }

    getGearSlower () {
        return get(this, 'vehicle.verticalData.gearSlower', null)
    }

    getCabinTypeId () {
        return get(this, 'vehicle.verticalData.cabinType.id', null)
    }

    getCabinType (langCode) {
        return get(this, `vehicle.verticalData.cabinType[${langCode}]`, null)
    }

    getCabinColorId () {
        return get(this, 'vehicle.verticalData.cabinColor.id', null)
    }

    getCabinColor (langCode) {
        return get(this, `vehicle.verticalData.cabinColor[${langCode}]`, null)
    }

    getCabinTypeAndColor (langCode) {
        const type = this.getCabinType(langCode)
        const color = this.getCabinColor(langCode)

        return type || color ? `${type || '- '}, ${color || '-'}` : null
    }

    getEmissionGradeId () {
        return get(this, 'vehicle.verticalData.emissionGrade.id', null)
    }

    getEmissionGrade () {
        return get(this, 'vehicle.verticalData.emissionGrade.name', null)
    }

    getSuspensionAndBreakType (langCode) {
        const suspension = this.getSuspensionType(langCode)
        const breaks = this.getBreakType(langCode)

        return suspension || breaks ? `${suspension || '- '}, ${breaks || '-'}` : null
    }

    /**
     * Accessories
     */

    getAccessories () {
        return get(this, 'vehicle.verticalData.accessories', null)
    }

    /**
     * Primary details
     */

    getPrimaryDetailsSummary () {
        return this.getMakeAndModelName() || '-'
    }

    getPrimaryDetailsValidationSchema () {
        return Nope.schema({
            vehicleType: Yup.string(),
            regNumber: Nope.registerNumber(),
            isFinnishRegNumber: Yup.boolean(),
            name: Nope.vehicleName(),
            make: Yup.string(),
            mfgYear: Yup.string().nullable(),
            model: Yup.string(),
            color: Yup.string(),
            colorType: Yup.string(),
            machine: Yup.object().shape({
                modelTypeName: Yup.string(),
            })
        })
    }

    getPrimaryDetailsInitialValues () {
        return {
            vehicleType: this.getVehicleTypeId() || '',
            make: this.getMakeId() || '',
            mfgYear: this.getYear() || '',
            name: this.vehicle.name || '',
            regNumber: this.vehicle.regNumber || '',
            color: this.getColorId() || '',
            colorType: this.getColorTypeId() || '',
            isFinnishRegNumber: this.getIsFinnishRegNumber(),
            machine: {
                modelTypeName: this.getModelTypeName() || '',
            }
        }
    }

    getPrimaryDetailsFieldsLayoutSchema () {
        return {
            layout: [
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['regNumber', 'isFinnishRegNumber', 'name'],
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['make', 'machine.modelTypeName', 'mfgYear'],
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['color', 'colorType'],
                },
            ]
        }
    }

    getPrimaryDetailsFieldsSchema (t) {
        return {
            fields: [
                {
                    name: 'regNumber',
                    type: 'string',
                    inputType: 'text',
                    fieldProps: {
                        label: t('vehicleProfile:regNumber'),
                        placeholder: 'ABC-123',
                        validate: (value, formValues) => this.validateRegNumber(value, formValues),
                    },
                },
                {
                    name: 'isFinnishRegNumber',
                    type: 'boolean',
                    inputType: 'checkbox',
                    fieldProps: {
                        label: t('vehicleProfile:sections.primary.isFinnishRegNumber'),
                        size: 'large'
                    }
                },
                {
                    name: 'name',
                    type: 'string',
                    inputType: 'text',
                    fieldProps: {
                        label: t('vehicleProfile:vehicleNickname'),
                        description: t('vehicleProfile:vehicleNicknameDescription'),
                        placeholder: getVehicleDisplayName(this.vehicle, t),
                    },
                },
                {
                    name: 'make',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        label: t('vehicleProfile:vehicleMake'),
                        emptyPlaceholder: t('vehicleProfile:sections.primary.empty.make'),
                        loadingPlaceholder: t('vehicleProfile:sections.primary.loading.make'),
                        placeholder: t('vehicleProfile:sections.primary.choose.make'),
                    },
                    resetFieldsOnChange: ['model'],
                    query: {
                        query: getOptionsMachineVehicleMake,
                        variables: ({ values }) => {
                            return {
                                id: parseInt(values.vehicleType)
                            }
                        },
                        getResultValue: (data) => {
                            const mostPopular = getVehicleMakeQuickOptions(get(data, 'options.machine.make', []), t)
                            const groupedMakes = getAlpahebeticalOptionGroups(get(data, 'options.machine.make', []), 'name')
                            return [...mostPopular, ...groupedMakes]
                        },
                    },
                },
                {
                    name: 'machine.modelTypeName',
                    type: 'string',
                    inputType: 'text',
                    fieldProps: {
                        label: t('vehicleProfile:vehicleModel'),
                        placeholder: t('vehicleProfile:vehicleModel'),
                    },
                },
                {
                    name: 'mfgYear',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'value',
                        label: t('vehicleProfile:vehicleYearModel'),
                        placeholder: t('vehicleProfile:sections.primary.choose.year'),
                        loadingPlaceholder: t('vehicleProfile:sections.primary.loading.year'),
                        emptyPlaceholder: t('vehicleProfile:sections.primary.empty.year'),
                        options: yearOptions,
                    },
                },
                {
                    name: 'color',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionNameKey: i18n.language,
                        optionValueKey: 'id',
                        label: t('vehicleProfile:color'),
                        placeholder: t('vehicleProfile:sections.primary.choose.color'),
                        loadingPlaceholder: t('vehicleProfile:sections.primary.loading.color'),
                        emptyPlaceholder: t('vehicleProfile:sections.primary.empty.color'),
                    },
                    query: {
                        query: getOptionsMachineVehicleCommon,
                        getResultValue: (data) => {
                            return get(data, 'options.machine.color', [])
                        },
                    },
                },
                {
                    name: 'colorType',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionNameKey: i18n.language,
                        optionValueKey: 'id',
                        label: t('vehicleProfile:colorType'),
                        placeholder: t('vehicleProfile:sections.primary.choose.colorType'),
                        loadingPlaceholder: t('vehicleProfile:sections.primary.loading.colorType'),
                        emptyPlaceholder: t('vehicleProfile:sections.primary.empty.colorType'),
                    },
                    query: {
                        query: getOptionsMachineVehicleCommon,
                        getResultValue: (data) => {
                            return get(data, 'options.machine.colorType', [])
                        },
                    },
                },
            ]
        }
    }

     /**
     * Technical details
     */

    getTechnicalDetailsValidationSchema () {
        return Nope.schema({
            power: Nope.positiveInteger(),
            torque: Nope.positiveInteger(),
            fuelType: Nope.positiveInteger(),
            gearType: Nope.positiveInteger(),
            driveType: Nope.positiveInteger(),
            engineSize: Nope.positiveNumber(),
            machine: Yup.object().shape({
                weight: Nope.positiveInteger(),
                vin: Yup.string(),
                rearAxelModel: Nope.positiveInteger(),
                cabinType: Nope.positiveInteger(),
                cabinColor: Nope.positiveInteger(),
                suspensionType: Nope.positiveInteger(),
                breakType: Nope.positiveInteger(),
                emissionGrade: Nope.positiveInteger(),
                isGearSlower: Yup.boolean(),
            })
        })
    }

    getTechnicalDetailsInitialValues () {
        return {
            power: this.getPower() || '',
            torque: this.getTorque() || '',
            fuelType: this.getFuelTypeId() || '',
            gearType: this.getGearTypeId() || '',
            driveType: this.getDriveTypeId() || '',
            engineSize: this.getEngineSize() || '',
            machine: {
                weight: this.getGrossWeight() || '',
                vin: this.getVinNumber() || '',
                rearAxelModel: this.getRearAxelId() || '',
                cabinType: this.getCabinTypeId() || '',
                cabinColor: this.getCabinColorId() || '',
                suspensionType: this.getSuspensionTypeId() || '',
                breakType: this.getBreakTypeId() || '',
                emissionGrade: this.getEmissionGradeId() || '',
                isGearSlower: this.getGearSlower() || false,
            }
        }
    }

    getTechnicalDetailsFieldsLayoutSchema (t) {
        return {
            layout: [
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['power', 'torque'],
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['engineSize', 'fuelType'],
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['driveType'],
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['machine.weight', 'machine.vin', 'machine.emissionGrade']
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: ['gearType', 'machine.isGearSlower']
                },
                {
                    type: 'formElementGroup',
                    classNames: 'cols-2',
                    fields: [
                        'machine.rearAxelModel',
                        'machine.cabinType',
                        'machine.cabinColor',
                        'machine.suspensionType',
                        'machine.breakType',
                    ]
                }
            ]
        }
    }

    getSubtypesTechnicalDetailsFieldsSchema (t) {
        let fields = []
        switch (this.getVehicleTypeId()) {
        case VEHICLE_TYPES.TRUCK:
        case VEHICLE_TYPES.BUS:
        case VEHICLE_TYPES.TRAILER:
            fields = [
                {
                    name: 'machine.emissionGrade',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        optionNameKey: 'name',
                        label: t('vehicleProfile:emissionGrade'),
                        emptyPlaceholder: t('vehicleProfile:sections.technical.empty.emissionGrade'),
                        loadingPlaceholder: t('vehicleProfile:sections.technical.loading.emissionGrade'),
                        placeholder: t('vehicleProfile:sections.technical.choose.emissionGrade'),
                    },
                    query: {
                        query: getOptionsMachineVehicleCommon,
                        getResultValue: (data) => {
                            return get(data, 'options.machine.emissionGrade', [])
                        },
                    },
                },
                {
                    name: 'gearType',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        optionNameKey: i18n.language,
                        label: t('vehicleProfile:gearType'),
                        emptyPlaceholder: t('vehicleProfile:sections.technical.empty.gearType'),
                        loadingPlaceholder: t('vehicleProfile:sections.technical.loading.gearType'),
                        placeholder: t('vehicleProfile:sections.technical.choose.gearType'),
                    },
                    query: {
                        query: getOptionsMachineVehicleCommon,
                        getResultValue: (data) => {
                            return get(data, 'options.machine.gearType', [])
                        },
                    },
                },
                {
                    name: 'machine.isGearSlower',
                    type: 'boolean',
                    inputType: 'checkbox',
                    fieldProps: {
                        label: t('vehicleProfile:gearSlower'),
                        size: 'large'
                    }
                },
                {
                    name: 'machine.rearAxelModel',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        optionNameKey: i18n.language,
                        label: t('vehicleProfile:rearAxel'),
                        emptyPlaceholder: t('vehicleProfile:sections.technical.empty.rearAxel'),
                        loadingPlaceholder: t('vehicleProfile:sections.technical.loading.rearAxel'),
                        placeholder: t('vehicleProfile:sections.technical.choose.rearAxel'),
                    },
                    query: {
                        query: getOptionsMachineVehicleCommon,
                        getResultValue: (data) => {
                            return get(data, 'options.machine.rearAxelModel', [])
                        },
                    },
                },
                {
                    name: 'machine.cabinType',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        optionNameKey: i18n.language,
                        label: t('vehicleProfile:cabinType'),
                        emptyPlaceholder: t('vehicleProfile:sections.technical.empty.cabinType'),
                        loadingPlaceholder: t('vehicleProfile:sections.technical.loading.cabinType'),
                        placeholder: t('vehicleProfile:sections.technical.choose.cabinType'),
                    },
                    query: {
                        query: getOptionsMachineVehicleCommon,
                        getResultValue: (data) => {
                            return get(data, 'options.machine.cabinType', [])
                        },
                    },
                },
                {
                    name: 'machine.cabinColor',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        optionNameKey: i18n.language,
                        label: t('vehicleProfile:cabinColor'),
                        emptyPlaceholder: t('vehicleProfile:sections.technical.empty.cabinColor'),
                        loadingPlaceholder: t('vehicleProfile:sections.technical.loading.cabinColor'),
                        placeholder: t('vehicleProfile:sections.technical.choose.cabinColor'),
                    },
                    query: {
                        query: getOptionsMachineVehicleCommon,
                        getResultValue: (data) => {
                            return get(data, 'options.machine.color', [])
                        },
                    },
                },
                {
                    name: 'machine.suspensionType',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        optionNameKey: i18n.language,
                        label: t('vehicleProfile:suspensionType'),
                        emptyPlaceholder: t('vehicleProfile:sections.technical.empty.suspensionType'),
                        loadingPlaceholder: t('vehicleProfile:sections.technical.loading.suspensionType'),
                        placeholder: t('vehicleProfile:sections.technical.choose.suspensionType'),
                    },
                    query: {
                        query: getOptionsMachineVehicleCommon,
                        getResultValue: (data) => {
                            return get(data, 'options.machine.suspensionType', [])
                        },
                    },
                },
                {
                    name: 'machine.breakType',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        optionNameKey: i18n.language,
                        label: t('vehicleProfile:breakType'),
                        emptyPlaceholder: t('vehicleProfile:sections.technical.empty.breakType'),
                        loadingPlaceholder: t('vehicleProfile:sections.technical.loading.breakType'),
                        placeholder: t('vehicleProfile:sections.technical.choose.breakType'),
                    },
                    query: {
                        query: getOptionsMachineVehicleCommon,
                        getResultValue: (data) => {
                            return get(data, 'options.machine.breakType', [])
                        },
                    },
                }
            ]
            break
        default:
            break
        }

        return fields
    }

    getTechnicalDetailsFieldsSchema (t) {
        return {
            fields: [
                {
                    name: 'power',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:power')} (kW)`,
                    },
                },
                {
                    name: 'torque',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:torque')} (Nm)`,
                    },
                },
                {
                    name: 'engineSize',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        label: t('vehicleProfile:engineSize'),
                        emptyPlaceholder: t('vehicleProfile:sections.primary.empty.engineSize'),
                        loadingPlaceholder: t('vehicleProfile:sections.primary.loading.engineSize'),
                        placeholder: t('vehicleProfile:sections.technical.choose.engineSize'),
                        options: [
                            { value: '0', name: t('vehicleProfile:sections.technical.noEngineSize') },
                            ...getEngineSizes(this.vehicle)
                        ]
                    },
                },
                {
                    name: 'fuelType',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        optionNameKey: i18n.language,
                        label: t('vehicleProfile:fuelType'),
                        emptyPlaceholder: t('vehicleProfile:sections.primary.empty.fuelType'),
                        loadingPlaceholder: t('vehicleProfile:sections.primary.loading.fuelType'),
                        placeholder: t('vehicleProfile:sections.technical.choose.fuelType'),
                    },
                    query: {
                        query: getOptionsMachineVehicleCommon,
                        getResultValue: (data) => {
                            return get(data, 'options.machine.fuelType', [])
                        },
                    },
                },
                {
                    name: 'driveType',
                    type: 'string',
                    inputType: 'select',
                    fieldProps: {
                        optionValueKey: 'id',
                        optionNameKey: i18n.language,
                        label: t('vehicleProfile:driveType'),
                        emptyPlaceholder: t('vehicleProfile:sections.primary.empty.driveType'),
                        loadingPlaceholder: t('vehicleProfile:sections.primary.loading.driveType'),
                        placeholder: t('vehicleProfile:sections.technical.choose.driveType'),
                    },
                    query: {
                        query: getOptionsMachineVehicleDriveType,
                        variables: ({ values }) => {
                            return {
                                id: this.getVehicleTypeId()
                            }
                        },
                        getResultValue: (data) => {
                            return get(data, 'options.machine.driveType', [])
                        },
                    },
                },
                {
                    name: 'machine.weight',
                    type: 'number',
                    inputType: 'number',
                    fieldProps: {
                        label: `${t('vehicleProfile:grossWeight')} (kg)`,
                    },
                },
                {
                    name: 'machine.vin',
                    type: 'text',
                    inputType: 'text',
                    fieldProps: {
                        label: t('vehicleProfile:chassisNumber')
                    }
                },
                ...this.getSubtypesTechnicalDetailsFieldsSchema(t),
            ],
        }
    }
}
